import * as React from "react";
import { Button, Checkbox, notification } from "antd";
import { Location } from "./../../schema/types/location/entity";
import { TableNewItemButton } from "../../newcomponents/table/components/tableNewButton";
import { TableExportButton } from "../../newcomponents/table/components/tableExportButton";
import { useTableKindId } from "../../newcomponents/table/data/useTableKindId";
import { useTableItemQueryData } from "../../newcomponents/table/data/useTableItemQueryData";
import { linksOf, request } from "../../services/cpln";
import { TableHeaderRefreshButton } from "../../newcomponents/table/components/RefreshButton";
import { CustomTableCPLNQuery } from "../../newcomponents/table/components/TableCplnQuery";
import { NameDescriptionColumn } from "../../newcomponents/table/columns/wellKnown/nameDescriptionColumn";
import { Table } from "../../newcomponents/table/table";
import { ProviderColumn } from "../../newcomponents/table/columns/wellKnown/providerColumn";
import { TagsColumn } from "../../newcomponents/table/columns/wellKnown/tagsColumn";
import { AxiosResponse } from "axios";
import objectHash from "object-hash";
import { useColumnsWithTagColumns } from "../../newcomponents/table/columns/tagColumns/useColumnsWithTagColumns";
import { LocationTableItem } from "../../mst/kinds/location.helpers";

interface LocationTableItemWithMeta extends LocationTableItem {
  uiMeta: {
    wasEnabled: boolean;
    enabled: boolean;
  };
}

export const LocationList: React.FC = () => {
  const { kind, id } = useTableKindId("location", "location-list");
  const qData = useTableItemQueryData<Location>(kind, {});
  const columns = useColumnsWithTagColumns<LocationTableItemWithMeta>(
    [
      NameDescriptionColumn(),
      ProviderColumn(),
      TagsColumn(),
      {
        id: "spec",
        label: "",
        cell: (p) => {
          const location = p.row.original;

          return (
            <div className="flex items-center justify-end w-full pr-4">
              <Checkbox
                checked={location.uiMeta.enabled}
                onChange={(e) => {
                  setEnableChanges((_enableChanges) => {
                    const enableChangesClone = JSON.parse(JSON.stringify(_enableChanges));
                    enableChangesClone[location.name] = e.target.checked;
                    return enableChangesClone;
                  });
                }}
              >
                Enabled
                <span className={`${location.uiMeta.wasEnabled !== location.uiMeta.enabled ? "" : "invisible"}`}>
                  *
                </span>
              </Checkbox>
            </div>
          );
        },
      },
    ],
    kind,
  );

  const [isLoadingEnableChanges, setIsLoadingEnableChanges] = React.useState(false);
  const [enableChanges, setEnableChanges] = React.useState<{ [_: string]: boolean }>({});

  const locations = React.useMemo<LocationTableItemWithMeta[]>(
    () =>
      qData.visibleItems.map((i) => {
        const wasEnabled = i.spec?.enabled || false;
        let enabled = wasEnabled;
        const change = Object.entries(enableChanges).find(([name]) => name === i.name);
        if (change) {
          const [_, value] = change;
          enabled = value;
        }
        return { ...i, selfLink: linksOf(i).self!, uiMeta: { wasEnabled: wasEnabled, enabled: enabled } };
      }),
    [qData.visibleItems, objectHash(enableChanges)],
  );

  const isDirty = React.useMemo(() => locations.find((i) => i.uiMeta.wasEnabled !== i.uiMeta.enabled), [locations]);

  async function onSaveEnabledChanges() {
    try {
      const promises: Promise<AxiosResponse>[] = [];

      for (const change of Object.entries(enableChanges)) {
        const [changeName, changeValue] = change;
        const l = qData.items.find((i) => i.name === changeName);
        if (!l) {
          continue;
        }
        const lValue = l.spec?.enabled || false;
        const value = changeValue;
        if (lValue !== value) {
          promises.push(request({ method: "patch", url: linksOf(l).self!, body: { spec: { enabled: value } } }));
        }
      }

      if (promises.length > 0) {
        setIsLoadingEnableChanges(true);
      }

      await Promise.all(promises);
      await qData.refetch();

      setIsLoadingEnableChanges(false);

      notification.success({
        message: "Success",
        description: "Updated location enabled changes",
      });
    } catch (e) {
      setIsLoadingEnableChanges(false);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  return (
    <>
      <Table<LocationTableItemWithMeta>
        tableId={id}
        title={"Locations"}
        data={locations}
        headerRenderer={(table) => {
          const currentVisibleItemLinks = table.getRowModel().rows.map((row) => linksOf(row.original).self!);
          return (
            <>
              <Button
                onClick={() => setEnableChanges({})}
                type="default"
                danger
                disabled={!isDirty || isLoadingEnableChanges}
              >
                Reset Changes
              </Button>
              <Button
                onClick={onSaveEnabledChanges}
                type="primary"
                disabled={!isDirty || isLoadingEnableChanges}
                loading={isLoadingEnableChanges}
              >
                Save Changes
              </Button>
              <TableNewItemButton kind={kind} />
              <TableExportButton selfLinks={currentVisibleItemLinks} kind={kind} />
              <TableHeaderRefreshButton onClick={qData.refetch} />
              <CustomTableCPLNQuery
                tableId={id}
                kind={kind}
                onQuery={qData.setCplnQuery}
                isLoading={qData.isLoading}
              />
            </>
          );
        }}
        enableFilter
        isLoading={qData.isLoading}
        onSortChange={qData.setSort}
        onSearchChange={qData.setSearch}
        isLoadingMore={qData.isLoadingMore}
        hasMore={qData.hasMore}
        onLoadMore={qData.fetchNextPage}
        columns={columns}
      />
    </>
  );
};
